import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  getSensorsInUseInPlannings,
  getTasksFromViewPlanning,
} from 'services/api';
import DataGrid from 'composant/UI/DataGrid/DataGrid';
import styles from './SensorSelector.module.scss';

const COLUMN_CONFIG: Array<[string, string, number]> = [
  ['sensor_id', 'ID du capteur', 140],
  ['sensor_name', 'Nom du capteur', 260],
];

const createColumn = (field: string, headerName: string, width: number) => ({
  field,
  headerName,
  width,
  sortable: true,
  align: 'center' as const,
  headerAlign: 'center' as const,
});

interface SensorItem {
  sensor_id: number;
  sensor_name: string;
  view_type: string;
}

interface SensorSelectorProps {
  sensorList: SensorItem[];
  selectedSensors: any;
  setSelectedSensors: (selected: any[]) => void;
  idView?: number;
}

const SensorSelector = (props: SensorSelectorProps) => {
  const viewList = useSelector((state: any) => state.vue.views_list);
  const { sensorList, selectedSensors, setSelectedSensors } = props;
  const [availableSensors, setAvailableSensors] =
    React.useState<SensorItem[]>(sensorList);
  const [alreadyUsedSensors, setAlreadyUsedSensors] = React.useState<string[]>(
    []
  );
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const checkSensorsAlreadyInUse = async (projectId: number) => {
      const responseGetAlreadyUsedSensors = await getSensorsInUseInPlannings({
        projectId,
      });
      const potentiallySensorInCurrentView: SensorItem[] = [];
      if (props.idView) {
        const responseGetTasksOfCurrentView = await getTasksFromViewPlanning({
          viewId: props.idView,
        });
        const usedInCurrentViewSensors =
          responseGetTasksOfCurrentView.data.tasks[0].sensors_list.split(',');

        sensorList.map((sensor: SensorItem) => {
          if (usedInCurrentViewSensors.includes('' + sensor.sensor_id)) {
            potentiallySensorInCurrentView.push(sensor);
          }
        });
        setSelectedSensors(
          selectedSensors.concat(potentiallySensorInCurrentView)
        );
      }
      const alreadyUsedSensors =
        responseGetAlreadyUsedSensors.data.sensors_ids?.filter(
          (sensorId: string) =>
            !potentiallySensorInCurrentView.some(
              (sensor: SensorItem) => sensor.sensor_id === parseInt(sensorId)
            )
        );
      setAlreadyUsedSensors(alreadyUsedSensors ? alreadyUsedSensors : []);
      setAvailableSensors(sensorList);
    };
    checkSensorsAlreadyInUse(viewList[0].project_id);
  }, [sensorList]);

  const columns = useMemo(
    () => [
      ...COLUMN_CONFIG.map(([field, headerName, width]) =>
        createColumn(field, headerName, width)
      ),
      {
        field: 'sensor_name',
        headerName: 'Nom du capteur',
        width: 260,
        sortable: true,
        flex: 1,
        renderCell: (params: any) => {
          const isUsed = alreadyUsedSensors?.includes(
            '' + params.row.sensor_id
          );
          return (
            <span
              style={{
                textDecoration: isUsed ? 'line-through' : 'none',
                color: isUsed ? '#999999' : 'inherit',
              }}
            >
              {params.value}
            </span>
          );
        },
      },
    ],
    [alreadyUsedSensors]
  );

  const selectionModel = useMemo(
    () => selectedSensors.map((sensor: SensorItem) => sensor.sensor_id),
    [selectedSensors]
  );

  const handleSelectionChange = useCallback(
    (newSelection: any[]) => {
      const selected = availableSensors.filter((sensor) =>
        newSelection.includes(sensor.sensor_id)
      );
      setSelectedSensors(selected);
    },
    [availableSensors, setSelectedSensors]
  );
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h6 className={styles.title}>Sélection des capteurs</h6>
        <div className={styles.infoWrapper}>
          <button
            type="button"
            className={styles.infoButton}
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo(!showInfo)}
          >
            i
          </button>
          {showInfo && (
            <div className={styles.infoTooltip}>
              <div className={styles.infoTitle}>
                ℹ️ Information sur les capteurs
              </div>
              <div className={styles.infoItem}>
                • <strong>Capteurs disponibles :</strong> Peuvent être
                sélectionnés
              </div>
              <div className={styles.infoItem}>
                • <strong>Capteurs grisés :</strong> Déjà utilisés dans d'autres
                planifications
              </div>
              <div className={styles.infoHint}>
                💡 Pour utiliser un capteur grisé, désélectionnez-le d'abord
                dans le projet où il est actuellement utilisé.
              </div>
              <div className={styles.infoArrow} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <DataGrid
          rows={availableSensors}
          columns={columns}
          getRowId={(row: any) => row.sensor_id}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectionModel}
          isRowSelectable={(params: any) =>
            !alreadyUsedSensors.includes('' + params.row.sensor_id)
          }
          pageSizeOptions={[10, 25, 50]}
          initialPageSize={10}
          autoHeight
        />
      </div>
    </div>
  );
};

export default SensorSelector;
